import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FiX, FiCheckCircle, FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import Confetti from 'react-confetti';
import useWindowSize from '@/hooks/useWindowSize';

interface ValidationFeedbackProps {
  messages: string[];
  type: 'success' | 'error';
  onClose: () => void;
  onNext?: () => void;
  hasNext?: boolean;
  autoProgressDelay?: number; // in milliseconds, default 30000
}

const ValidationFeedback: React.FC<ValidationFeedbackProps> = ({
  messages,
  type,
  onClose,
  onNext,
  hasNext = false,
  autoProgressDelay = 30000,
}) => {
  const { t } = useTranslation();
  const { width, height } = useWindowSize();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [canProgress, setCanProgress] = useState(false);

  // Start confetti immediately on success
  useEffect(() => {
    if (type === 'success') {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [type]);

  // Allow auto-progress only after initial delay
  useEffect(() => {
    if (type === 'success' && hasNext) {
      // Wait 5 seconds before showing auto-progress
      const timer = setTimeout(() => {
        setCanProgress(true);
        setTimeLeft(autoProgressDelay);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [type, hasNext, autoProgressDelay]);

  // Start countdown only after we can progress
  useEffect(() => {
    if (timeLeft === null || !canProgress) return;

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === null || prev <= 1000) {
          clearInterval(interval);
          if (prev !== null && prev <= 1000 && onNext) {
            onNext();
          }
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, canProgress, onNext]);

  const handleClose = useCallback(() => {
    setTimeLeft(null);
    setCanProgress(false);
    onClose();
  }, [onClose]);

  const cancelAutoProgress = useCallback(() => {
    setTimeLeft(null);
    setCanProgress(false);
  }, []);

  const handleNext = useCallback(() => {
    if (onNext) {
      setTimeLeft(null);
      setCanProgress(false);
      onNext();
    }
  }, [onNext]);

  const Icon = type === 'success' ? FiCheckCircle : FiAlertCircle;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={700}
          gravity={0.3}
          initialVelocityY={30}
          initialVelocityX={10}
          origin={{ x: 0.5, y: 1.2 }}
          colors={['#ff2b94', '#00ffea', '#ffea00', '#ff00ff', '#00ff95', '#4ecdc4']}
        />
      )}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      
      {/* Cyberpunk-styled modal */}
      <div className={`
        relative bg-base-100/90 rounded-lg  
        w-[calc(100%-2rem)] max-w-sm mx-auto
        transform transition-all duration-300 ease-out scale-100
        border ${type === 'success' ? 'border-success' : 'border-error'}
        shadow-[0_0_30px_rgba(${type === 'success' ? '0,255,128' : '255,0,0'},0.5)]
        backdrop-blur-md overflow-hidden
      `}>
        {/* Background circuit pattern */}
        <div className="absolute inset-0 bg-circuit-pattern opacity-10"></div>
        
        {/* Animated corner diagonal lines */}
        <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
          <div className={`absolute -top-8 -right-8 w-16 h-16 rotate-45 ${type === 'success' ? 'bg-success' : 'bg-error'} opacity-10`}></div>
        </div>
        <div className="absolute bottom-0 left-0 w-16 h-16 overflow-hidden">
          <div className={`absolute -bottom-8 -left-8 w-16 h-16 rotate-45 ${type === 'success' ? 'bg-success' : 'bg-error'} opacity-10`}></div>
        </div>
        
        {/* Animated border glow */}
        <div className={`absolute inset-0 rounded-lg ${type === 'success' ? 'animate-pulse-success' : 'animate-pulse-error'} opacity-0`}></div>
        
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center hover:bg-base-200/50 rounded-full transition-colors z-10"
          aria-label={t('common.close')}
        >
          <FiX className="w-4 h-4" />
        </button>
        
        <div className="p-6 relative z-10">
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-lg ${type === 'success' ? 'bg-success/10' : 'bg-error/10'} flex items-center justify-center flex-shrink-0`}>
              <Icon 
                className={`w-5 h-5 ${type === 'success' ? 'text-success' : 'text-error'}`} 
              />
            </div>
            
            <div className="min-w-0 flex-1">
              <div className="glitch-container">
                <h3 
                  data-text={t(type === 'success' ? 'learn.validation.successMessage' : 'learn.validation.errorMessage')}
                  className={`text-lg font-bold font-tech-secondary ${type === 'success' ? 'text-success' : 'text-error'}`}
                >
                  {t(type === 'success' ? 'learn.validation.successMessage' : 'learn.validation.errorMessage')}
                </h3>
              </div>

              {type === 'error' && messages.length > 0 && (
                <div className="mt-4 space-y-2">
                  {messages.map((message, index) => (
                    <p key={index} className="text-sm text-base-content/80 break-words">
                      {message}
                    </p>
                  ))}
                </div>
              )}

              {type === 'success' && (
                <div className="mt-2">
                  <div className={`text-sm text-base-content/80 ${hasNext ? 'mb-6' : ''}`}>
                    {t('learn.validation.successDetail', "You've successfully completed this tutorial!")}
                  </div>
                  
                  {hasNext && canProgress && timeLeft !== null && (
                    <div className="mt-5 flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div className="w-full bg-base-300/50 h-2 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary/70"
                            style={{ width: `${(timeLeft / autoProgressDelay) * 100}%`, transition: 'width 1s linear' }} 
                          />
                        </div>
                        <span className="text-xs text-base-content/70 ml-3 min-w-8 text-right">
                          {Math.ceil(timeLeft / 1000)}s
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={cancelAutoProgress}
                          className="btn btn-ghost btn-sm"
                        >
                          {t('common.cancel')}
                        </button>
                        
                        <button
                          onClick={handleNext}
                          className="btn btn-primary btn-sm gap-2 flex-1 neon-glow"
                        >
                          {t('learn.nextTutorial')}
                          <FiArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationFeedback;
